/* eslint-disable react/jsx-fragments */
import {
  ColorName,
  Colors,
  IconButton,
  IconButtonProps,
} from '@zapier/design-system';
import { clsx } from 'clsx';
import { Fragment, ReactNode, useCallback, useRef, useState } from 'react';
import styles from './Carousel.module.scss';

type CarouselProps = {
  /** The content to render within the carousel. */
  children: ReactNode;
  /** Id for the carousel div. */
  id?: string;
  /** Role for the carousel div. */
  role?: string;
  /** Width of each element in the carousel. */
  elementWidth?: number;
  /** Gap between elements in the carousel. */
  gap?: number;
  /** Color of the arrow buttons for sliding left and right. */
  arrowButtonColor?: IconButtonProps['variant'];
  /** Size of the arrow buttons for sliding left and right. */
  arrowButtonSize?: IconButtonProps['size'];
  /** Background color of the page the carousel will live on. */
  pageBackgroundColor?: ColorName;
  /** Width of the gutter of the page the carousel will live on. Used to make the carousel full-width on mobile. */
  pageGutterWidth?: number;
  /** Bottom padding of the carousel. Defaults to 12px to allow drop shadows on elements to be visible. */
  paddingBottom?: number;
  /** Flag to temporarily disable the scroll buttons, such as during loading or deletion of content. */
  disableScrollButtons?: boolean;
  /** Function to call when the left arrow is clicked. */
  onLeftArrowClick?: () => void;
  /** Function to call when the right arrow is clicked. */
  onRightArrowClick?: () => void;
  /** Optional property for overriding styles. */
  className?: string;
};
/** `Carousel` component for rendering a scrollable list of children in a carousel style. */
export const Carousel = ({
  children,
  id,
  role,
  elementWidth = 300,
  gap = 20,
  arrowButtonColor = 'tertiary',
  arrowButtonSize = 'medium',
  pageBackgroundColor = 'BackgroundWeaker',
  pageGutterWidth = 20,
  paddingBottom = 12,
  disableScrollButtons = false,
  onLeftArrowClick,
  onRightArrowClick,
  className,
}: CarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [canScrollRightInitial, setCanScrollRightInitial] =
    useState<boolean>(false);

  // This is to check if the carousel can scroll right initially because the scrollContainerRef is not available on first render.
  const carouselRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setCanScrollRightInitial(
        node.scrollWidth > node.getBoundingClientRect().width
      );
    }
  }, []);

  const handleLeftArrowClick = (): void => {
    scrollContainerRef.current?.scrollBy({
      left: scrollContainerRef.current
        ? -Math.round(scrollContainerRef.current.clientWidth * 0.75)
        : -elementWidth,
      behavior: 'smooth',
    });
    onLeftArrowClick?.();
  };

  const handleRightArrowClick = (): void => {
    scrollContainerRef.current?.scrollBy({
      left: scrollContainerRef.current
        ? Math.round(scrollContainerRef.current.clientWidth * 0.75)
        : elementWidth,
      behavior: 'smooth',
    });
    onRightArrowClick?.();
  };

  const scrollHandler = () => {
    setScrollPosition(scrollContainerRef.current?.scrollLeft ?? 0);
  };

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollContainerRef.current
    ? scrollPosition + scrollContainerRef.current?.offsetWidth <
      scrollContainerRef.current?.scrollWidth
    : canScrollRightInitial;

  return (
    <div
      className={clsx(className, styles.carousel)}
      style={{
        '--page-background-color': Colors[pageBackgroundColor],
        '--gutter-width': `${pageGutterWidth}px`,
        '--element-gap': `${gap}px`,
        '--padding-bottom': `${paddingBottom}px`,
      }}
      id={id}
      role={role}
    >
      {canScrollLeft && (
        <Fragment>
          <div className={styles.leftGradient} />
          <div className={clsx(styles.arrowButton, styles.leftArrow)}>
            <IconButton
              name="arrowSmallLeft"
              variant={arrowButtonColor}
              size={arrowButtonSize}
              aria-label="Scroll right through carousel"
              isDisabled={disableScrollButtons}
              onPress={handleLeftArrowClick}
              style={{ borderRadius: '50%' }}
            />
          </div>
        </Fragment>
      )}
      {canScrollRight && (
        <Fragment>
          <div className={styles.rightGradient} />
          <div className={clsx(styles.arrowButton, styles.rightArrow)}>
            <IconButton
              name="arrowSmallRight"
              variant={arrowButtonColor}
              size={arrowButtonSize}
              aria-label="Scroll left through carousel"
              isDisabled={disableScrollButtons}
              onPress={handleRightArrowClick}
              style={{ borderRadius: '50%' }}
            />
          </div>
        </Fragment>
      )}
      <div
        className={styles.scrollableContainer}
        ref={scrollContainerRef}
        onScroll={scrollHandler}
      >
        <div
          ref={carouselRef}
          className={clsx(
            styles.carouselStyles,
            canScrollRightInitial && styles.rightArrowSpace
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
